import { ANSWER_WIDGET_TYPE } from "@kourai/api/home-answer";
import type { Board } from "@kourai/api/home-board";
import { createContext, type ReactNode, useCallback, useContext, useMemo } from "react";
import { useApi } from "@/lib/api-client";
import { addWidget, widgetId } from "@/lib/home/layout";
import type { PinnedAnswer } from "@/lib/home/pin";
import { newInstance } from "@/lib/home/registry";
import { widgets } from "@/widgets";

type PinToHome = (answer: PinnedAnswer, model: string) => Promise<boolean>;

const PinContext = createContext<PinToHome | null>(null);

/** "Pin to home" for the chat's answers, where there's a board to pin to. Null elsewhere. */
export const usePinToHome = () => useContext(PinContext);

/**
 * Lets answers be pinned to the board shown with this chat (ADMIN-266). The answer's content is
 * stored first, then the widget is added, so the board's save never sees an empty answer.
 */
export function PinToHomeProvider({
  workspaceId,
  update,
  children,
}: {
  workspaceId: string | null;
  update: (edit: (board: Board) => Board) => void;
  children: ReactNode;
}) {
  const api = useApi();
  const pin = useCallback<PinToHome>(
    async (answer, model) => {
      const def = widgets.get(ANSWER_WIDGET_TYPE);
      if (!workspaceId || !def) return false;
      const id = widgetId();
      const data = {
        markdown: answer.markdown,
        ...(answer.visual ? { visual: answer.visual } : {}),
        answeredAt: new Date().toISOString(),
      };
      const res = await api.api.workspaces[":id"].home.widgets[":widgetId"].data
        .$put(
          { param: { id: workspaceId, widgetId: id }, json: { type: def.type, data } } as never,
          { headers: { "If-Match": '"0"' } },
        )
        .catch(() => null);
      if (!res?.ok) return false;
      const instance = newInstance(def, id);
      update((b) =>
        addWidget(b, {
          ...instance,
          config: {
            ...instance.config,
            title: answer.title,
            prompt: answer.prompt,
            model,
            refreshHours: 0,
          },
        }),
      );
      return true;
    },
    [api, workspaceId, update],
  );
  const value = useMemo(() => (workspaceId ? pin : null), [workspaceId, pin]);
  return <PinContext.Provider value={value}>{children}</PinContext.Provider>;
}
